import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { WEBINAR_API } from "../constants";
import { buildSessionPayload } from "../payloads/buildSessionPayload";
import { webinarService } from "../services/webinarService";
import { getSessionActions } from "../status/getSessionActions";
import { SessionMode, SessionSaveAction } from "../types/actions";
import { WebinarServiceError } from "../types/dto";
import { WebinarHost, WebinarSession, WebinarTopic } from "../types/models";
import {
  SessionFormState,
  blankSessionForm,
  formForDuplicate,
  formFromSession,
} from "../utils/sessionFormState";
import {
  hasHostClash,
  validateDraft,
  validatePublish,
  validateSessionStep,
} from "../utils/sessionValidation";
import { logWebinarPayload } from "../utils/logWebinarPayload";

function toastDuplicateHint() {
  toast.info("Copied everything except the date — pick a new slot");
}

function errorMessage(e: unknown, fallback: string): string {
  if (e instanceof WebinarServiceError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

type Options = {
  /** When false, skip loading (drawer closed). Defaults to true. */
  open?: boolean;
  mode: SessionMode;
  /** Clicked row for edit/duplicate — no getSession refetch. */
  initialSession?: WebinarSession | null;
  /** Already-loaded list (for host clash). */
  existingSessions?: WebinarSession[];
  onSuccess?: (session: WebinarSession, action: SessionSaveAction) => void;
};

export function useSessionForm({
  open = true,
  mode,
  initialSession = null,
  existingSessions = [],
  onSuccess,
}: Options) {
  const [form, setForm] = useState<SessionFormState>(blankSessionForm());
  const [step, setStep] = useState(0);
  const [hosts, setHosts] = useState<WebinarHost[]>([]);
  const [topics, setTopics] = useState<WebinarTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [bootError, setBootError] = useState<string | null>(null);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setDirty(false);
      setError(null);
      setBootError(null);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setBootError(null);
      setDirty(false);
      setError(null);
      try {
        // Only catalogs are fetched here — session comes from the clicked row.
        const [hostsList, topicsList] = await Promise.all([
          webinarService.listHosts(),
          webinarService.listTopics(),
        ]);
        if (cancelled) return;
        setHosts(hostsList);
        setTopics(topicsList);

        if (mode === "create") {
          setForm(blankSessionForm());
          setStep(0);
          return;
        }

        if (!initialSession) {
          setBootError("Missing session");
          return;
        }

        if (mode === "edit") {
          let session = initialSession;
          const thinRow =
            !session.joinUrl?.trim() ||
            !session.hostId?.trim() ||
            !session.topicId?.trim();
          if (thinRow && session.id) {
            const fetched = await webinarService.getSession(session.id);
            if (fetched) session = fetched;
          }

          const actions = getSessionActions(session);
          if (!actions.canEdit) {
            setBootError(
              session.status === "live" && session.backendStatus !== "scheduled"
                ? "Live sessions cannot be edited"
                : "This session cannot be edited"
            );
            return;
          }
          setForm(formFromSession(session));
          setStep(0);
        } else {
          setForm(formForDuplicate(initialSession));
          setStep(3);
          toastDuplicateHint();
        }
      } catch (e) {
        if (!cancelled) {
          setBootError(errorMessage(e, "Failed to load session"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, mode, initialSession]);

  const setField = useCallback(
    <K extends keyof SessionFormState>(key: K, value: SessionFormState[K]) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setDirty(true);
      setError(null);
    },
    []
  );

  const publishValidation = useMemo(
    () => validatePublish(form, existingSessions),
    [form, existingSessions]
  );
  const hostClash = useMemo(
    () => hasHostClash(form, existingSessions),
    [form, existingSessions]
  );

  const footerFlags = useMemo(() => {
    if (mode === "create" || mode === "duplicate") {
      return {
        showDraft: true,
        showPublish: WEBINAR_API.canPublish,
        publishLabel: "Publish session",
      };
    }
    if (form.currentStatus === "scheduled") {
      return {
        showDraft: false,
        showPublish: true,
        publishLabel: "Save changes",
      };
    }
    return {
      showDraft: true,
      showPublish: WEBINAR_API.canPublish,
      publishLabel: "Publish session",
    };
  }, [mode, form.currentStatus]);

  const persistDraft = useCallback(async (): Promise<WebinarSession | null> => {
    if (savingRef.current) return null;
    setError(null);

    const d = validateDraft(form);
    if (!d.ok) {
      setError(d.message || "Invalid draft");
      return null;
    }

    // Scheduled sessions cannot be saved "as draft"; PATCH still uses full snapshot.
    const action: SessionSaveAction =
      mode === "edit" && form.currentStatus === "scheduled"
        ? "publish"
        : "draft";

    savingRef.current = true;
    setSaving(true);
    try {
      const payload = buildSessionPayload(form, { mode, action });
      logWebinarPayload(
        action === "draft"
          ? `session · ${mode} · draft`
          : `session · ${mode} · save`,
        payload
      );
      const saved = await webinarService.saveSession(payload);
      setForm((prev) => ({
        ...prev,
        id: saved.id,
        currentStatus: saved.status,
      }));
      setDirty(false);
      return saved;
    } catch (e) {
      const message = errorMessage(e, "Failed to save session");
      setError(message);
      toast.error(message);
      return null;
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [form, mode]);

  const goNext = useCallback(async () => {
    setError(null);
    const stepCheck = validateSessionStep(form, step);
    if (!stepCheck.ok) {
      setError(stepCheck.message || "Complete required fields");
      return;
    }
    const saved = await persistDraft();
    if (!saved) return;
    setStep((s) => Math.min(3, s + 1));
  }, [persistDraft, form, step]);

  const submit = useCallback(
    async (action: SessionSaveAction) => {
      if (savingRef.current) return;
      setError(null);

      // Scheduled edit "Save changes" — same checklist as publish, then PATCH (not /publish).
      if (
        action === "publish" &&
        mode === "edit" &&
        form.currentStatus === "scheduled"
      ) {
        const v = validatePublish(form);
        if (!v.isValid) {
          setError("Complete the publish checklist before continuing");
          setStep(3);
          return;
        }
        const saved = await persistDraft();
        if (saved) {
          onSuccess?.(saved, "publish");
        }
        return;
      }

      if (action === "publish") {
        if (!WEBINAR_API.canPublish) {
          toast.info("Publishing is not available yet");
          return;
        }
        const v = validatePublish(form);
        if (!v.isValid) {
          setError("Complete the publish checklist before continuing");
          setStep(3);
          return;
        }

        // Persist latest draft, then call publish endpoint.
        const draft = await persistDraft();
        if (!draft?.id) return;

        savingRef.current = true;
        setSaving(true);
        try {
          const published = await webinarService.publishSession(draft.id);
          setForm((prev) => ({
            ...prev,
            id: published.id,
            currentStatus: published.status,
          }));
          setDirty(false);
          onSuccess?.(published, "publish");
        } catch (e) {
          const message = errorMessage(e, "Failed to publish session");
          setError(message);
          toast.error(message);
        } finally {
          savingRef.current = false;
          setSaving(false);
        }
        return;
      }

      const saved = await persistDraft();
      if (saved) {
        toast.success("Saved as draft");
        onSuccess?.(saved, "draft");
      }
    },
    [form, mode, onSuccess, persistDraft]
  );

  return {
    form,
    setForm,
    setField,
    step,
    setStep,
    goNext,
    hosts,
    setHosts,
    topics,
    setTopics,
    sessions: existingSessions,
    loading,
    saving,
    error,
    setError,
    dirty,
    bootError,
    publishValidation,
    hostClash,
    footerFlags,
    submit,
    persistDraft,
  };
}
